import React from 'react';
import PropTypes from 'prop-types';
import Tabs from '@mulesoft/anypoint-components/lib/Tabs';
import TabList from '@mulesoft/anypoint-components/lib/TabList';
import Tab from '@mulesoft/anypoint-components/lib/Tab';
import TabPanel from '@mulesoft/anypoint-components/lib/TabPanel';
import { getStagingAgentsByType } from '../../../utils/utils';
import TabPanelReview from './TabPanelReview';
import { tabTheme } from '~/components/AgentScanners/types/commonTypes';
import styles from './Review.css';

const Review = ({ stagingAgents, region }) => {
  const publishedAgents = getStagingAgentsByType(stagingAgents, 'PUBLISHED'); // IMPORTED
  const updatedAgents = getStagingAgentsByType(stagingAgents, 'UPDATED'); // UPDATED
  const deletedAgents = getStagingAgentsByType(stagingAgents, 'DELETED'); // REMOVED
  const skippedAgents = getStagingAgentsByType(stagingAgents, 'SKIPPED'); // ALREADY
  const failedAgents = getStagingAgentsByType(stagingAgents, 'FAILED'); // UNSUCCESSFUL
  const pendingAgents = getStagingAgentsByType(stagingAgents, 'PENDING'); // UNSUCCESSFUL
  // Joins Failed and Pending agents
  const unsuccessfulAgents = [...failedAgents, ...pendingAgents];

  return (
    <div className={styles.detailSection} data-test-id="scan-review-section">
      <h2>Scan Review</h2>
      <Tabs>
        <TabList>
          <Tab testId="test-tab-imported" theme={tabTheme}>
            Added ({publishedAgents.length})
          </Tab>
          <Tab testId="test-tab-updated" theme={tabTheme}>
            Updated ({updatedAgents.length})
          </Tab>
          <Tab testId="test-tab-removed" theme={tabTheme}>
            Removed ({deletedAgents.length})
          </Tab>
          <Tab testId="test-tab-already" theme={tabTheme}>
            Already in Exchange ({skippedAgents.length})
          </Tab>
          <Tab
            testId="test-tab-not-updated"
            theme={tabTheme}
            display-if={unsuccessfulAgents?.length > 0}
          >
            Unsuccessful ({unsuccessfulAgents?.length})
          </Tab>
        </TabList>
        <TabPanel>
          <TabPanelReview
            title="Imported"
            subtitle="These agents were found in the external system but didn’t exist in Exchange."
            typeStyle={styles.reviewImported}
            agents={publishedAgents}
            region={region}
            typeMsg="New asset"
            typeMsgStyle={styles.published}
          />
        </TabPanel>
        <TabPanel>
          <TabPanelReview
            title="Update existing agents"
            subtitle="These agents exist in your Exchange but have newer versions available."
            typeStyle={styles.reviewUpdated}
            agents={updatedAgents}
            region={region}
            typeMsg="Update available"
            typeMsgStyle={styles.updated}
          />
        </TabPanel>
        <TabPanel>
          <TabPanelReview
            title="Remove missing agents"
            subtitle="These agents exist in your Exchange but were not found in the source system."
            typeStyle={styles.reviewRemove}
            agents={deletedAgents}
            typeMsg="Missing from source"
            typeMsgStyle={styles.deleted}
          />
        </TabPanel>
        <TabPanel>
          <TabPanelReview
            title="Review current agents"
            subtitle="These agents are already up-to-date in your Exchange."
            typeStyle={styles.reviewCurrent}
            agents={skippedAgents}
            region={region}
            typeMsg="Up to date"
            typeMsgStyle={styles.review}
          />
        </TabPanel>
        <TabPanel display-if={unsuccessfulAgents?.length > 0}>
          <TabPanelReview
            title="Unsuccessful"
            subtitle="These agents were not imported due to an issue. It will be reattempted in the next scan."
            typeStyle={styles.reviewUnsuccessful}
            agents={unsuccessfulAgents}
            region={region}
            typeMsg="Unsuccessful"
            typeMsgStyle={styles.unsuccessful}
            displayWarning
          />
        </TabPanel>
      </Tabs>
    </div>
  );
};

Review.propTypes = {
  stagingAgents: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.string,
      startedAt: PropTypes.string,
      endedAt: PropTypes.string,
      createdAt: PropTypes.string,
      summary: PropTypes.shape({
        added: PropTypes.number,
        updated: PropTypes.number,
        removed: PropTypes.number,
        unchanged: PropTypes.number,
        issuesFound: PropTypes.arrayOf(PropTypes.string)
      })
    })
  ),
  region: PropTypes.string
};

export default Review;
